import { useState, useEffect, useContext, useRef, useMemo } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useRecoilState, useRecoilValue } from "recoil";

import { createOpenSeadragon } from "../viewer/Custom-OpenSeadragon";

import { currentPostAtom, currentSlideAtom } from "../../atom";
import { ViewerContext } from "../../page/Viewer";
import { getSlideData, postAnnotation } from "../../api/slideAPI";
import { getSlideMpp } from "../../api/deepzoomAPI";
import { patchUserCustomSetting } from "../../api/user";
import styled from "styled-components";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "../viewer/AnnotationInfoPanel";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { ViewerToggleManager } from "../button";
import { Button, notification } from "antd";
import { hasTempViewerSession } from "../../api/api";
import { ensureTempViewerIdentity } from "../../lib/tempViewer";

interface Props {
  postId: number;
  description: string | undefined;
  annotationList: any[];
  setAnnotationList: React.Dispatch<React.SetStateAction<any[]>>;
  loading?: boolean;
  isSharedView?: boolean;
}

const buildAnnotationPathList = (annotationList?: any[]) => {
  if (!annotationList?.length) {
    return [];
  }

  return annotationList.reduce<any[]>((acc, path) => {
    if (!path?.annoInfo) {
      return acc;
    }

    const annoInfo =
      typeof structuredClone === "function"
        ? structuredClone(path.annoInfo)
        : JSON.parse(JSON.stringify(path.annoInfo));

    if (annoInfo && Array.isArray(annoInfo) && annoInfo[1]) {
      annoInfo[1].id = `annotation${path.annoId}`;
      annoInfo[1].label = path.content;
      acc.push(annoInfo);
    }

    return acc;
  }, []);
};

const ConsultationViewer = ({
  postId,
  description,
  annotationList,
  setAnnotationList,
  loading,
  isSharedView = false,
}: Props) => {
  const [viewer, setViewer] = useState<any>(null);
  const viewerRef = useRef<any>(null);
  const [icc, setIcc] = useState("GT450");
  const { userCustomSet, setUserCustomSet } = useContext(ViewerContext);

  const [currentSlide, setCurrentSlide] = useRecoilState(currentSlideAtom);
  const currentPost = useRecoilValue(currentPostAtom);

  const annotationInfo = useAnnotationInfo({
    currentSlide,
    setCurrentSlide: (data: any) => {
      setCurrentSlide(data);
      if (data?.annotations) {
        setAnnotationList(data.annotations);
      }
    },
    skipRefresh: true,
    customAnnotations: annotationList,
    requirePasswordOnEdit: isSharedView,
    onAnnotationsRefresh: (annotations) => {
      setAnnotationList(annotations);
      const pathList = buildAnnotationPathList(annotations);
      const v = viewerRef.current;
      if (v && v.areAnnotationsActive?.()) {
        v.setAnnotation(pathList);
      }
    },
  });

  const annotationListRef = useRef(annotationList);
  const annotationInfoRef = useRef(annotationInfo);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const currentSlideRef = useRef(currentSlide);
  const currentPostRef = useRef(currentPost);
  const userCustomSetRef = useRef(userCustomSet);
  const annotationPathList = useMemo(
    () => buildAnnotationPathList(annotationList),
    [annotationList]
  );

  useEffect(() => {
    annotationListRef.current = annotationList;
  }, [annotationList]);

  useEffect(() => {
    annotationInfoRef.current = annotationInfo;
  }, [annotationInfo]);

  useEffect(() => {
    currentSlideRef.current = currentSlide;
  }, [currentSlide]);

  useEffect(() => {
    currentPostRef.current = currentPost;
  }, [currentPost]);

  useEffect(() => {
    userCustomSetRef.current = userCustomSet;
  }, [userCustomSet]);

  useEffect(() => {
    notification.config({
      top: isSharedView ? 8 : 150,
      maxCount: 3,
    });
  }, [isSharedView]);

  const handleAnnotationClickWrapper = (event: any) => {
    let target = event.target;
    let annotationId = target.id;

    if (!annotationId && target.parentElement) {
      target = target.parentElement;
      annotationId = target.id;
    }

    if (annotationId && annotationId.startsWith('annotation')) {
      event.stopPropagation();
      const id = parseInt(annotationId.replace('annotation', ''));

      const annotation = annotationListRef.current.find(
        (anno: any) => anno.annoId === id
      );

      if (annotation) {
        const viewerContainer = document.getElementById('openseadragon');
        if (viewerContainer) {
          const containerRect = viewerContainer.getBoundingClientRect();
          const x = event.clientX - containerRect.left + 50;
          const infoPanelHeight = 150;
          const y = event.clientY - containerRect.top - (infoPanelHeight / 3);
          annotationInfoRef.current.setInfoPanelPosition({ x, y });
        }

        annotationInfoRef.current.setSelectedAnnotation(annotation);
        annotationInfoRef.current.setAnnotationInfoOpen(true);
      }
    }
  };

  const isImage = !currentSlide?.filename?.includes('.mrxs') &&
                  !currentSlide?.filename?.includes('.ndpi') &&
                  !currentSlide?.filename?.includes('.svs');

  useEffect(() => {
    isImage ? setIcc("none") : setIcc("GT450");
  }, [isImage]);

  const changeColor = () => {
    if (isImage) {
      notification.warning({
        key: 'image-icc-notification-warning',
        message: '알림',
        description: '이미지 파일은 ICC 프로파일을 적용할 수 없습니다.',
        placement: 'topRight',
        duration: 3,
        className: 'notification-top-layer',
      });
      return;
    }

    if (icc === "none") setIcc("GT450");
    else setIcc("none");
  };

  const getAnnotationList = async () => {
    if (!userCustomSetRef.current?.useAnnotation) {
      return null;
    }
    const data = await getSlideData(currentSlideRef.current.slideId);
    setAnnotationList(data.annotations);
    return buildAnnotationPathList(data.annotations);
  };

  const saveAnnotation = async (
    annotationPath: any,
    annotationName: any,
    rulerValue: any,
  ) => {
    const tempViewerIdentity = hasTempViewerSession()
      ? ensureTempViewerIdentity()
      : null;

    if (hasTempViewerSession() && !tempViewerIdentity) {
      return;
    }

    if (rulerValue)
      await postAnnotation(
        currentSlideRef.current.slideId,
        annotationPath,
        `${annotationName} / (${rulerValue}mm)`,
        tempViewerIdentity ?? undefined,
      );
    else
      await postAnnotation(
        currentSlideRef.current.slideId,
        annotationPath,
        annotationName,
        tempViewerIdentity ?? undefined,
      );
  };

  const getMpp = async () => {
    return await getSlideMpp(currentPostRef.current.id, currentSlideRef.current?.filename);
  };

  useEffect(() => {
    if (viewer && currentSlide?.filename) {
      const sourceUrl =
        process.env.REACT_APP_DEEPZOOM_URL +
        `/${icc}/${postId}/${currentSlide?.filename}.dzi`;

      if (viewer.currentSourceUrl !== sourceUrl) {
        viewer.currentSourceUrl = sourceUrl;
        viewer.open(sourceUrl);
      }
    }
  }, [viewer, icc, postId, currentSlide?.filename]);

  // useAnnotation 토글 시 뷰어 어노테이션 표시/숨김
  useEffect(() => {
    if (!viewer || !viewer.areAnnotationsActive?.()) return;
    if (!userCustomSet?.useAnnotation) {
      viewer.cleanAnnotations?.();
    }
  }, [viewer, userCustomSet?.useAnnotation]);

  useEffect(() => {
    const newViewer = createOpenSeadragon({
      showScalebar: true,
      useScaleBar: userCustomSet?.useScaleBar ?? true,
      useMagnificationBar: userCustomSet?.useMagnificationBar ?? true,
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? false,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      isPlainImage: isImage,
      saveAnnotation: true,
      getSlideMpp: getMpp,
      getAnnotations: getAnnotationList,
      addAnnotation: saveAnnotation,
      annotationClickEvent: handleAnnotationClickWrapper,
      isAnno: true,
      useInvertedZoom: userCustomSet ? userCustomSet.useInvertedZoom : false,
      slideTitle: description
    });

    viewerRef.current = newViewer;
    setViewer(newViewer);

    return () => {
      viewerRef.current = null;
      newViewer.destroy();
    };
  }, []);

  useEffect(() => {
    if (!viewer) return;

    viewer.updateRuntimeOptions?.({
      useScaleBar: userCustomSet?.useScaleBar ?? true,
      useMagnificationBar: userCustomSet?.useMagnificationBar ?? true,
      useRotationSlider: userCustomSet?.useViewerRotationSlider ?? true,
      useFiltering: userCustomSet?.useGammaSlider ?? true,
      showNavigator: userCustomSet?.useViewerThumbnail ?? false,
      useCapture: userCustomSet?.useCaptureButton ?? true,
      useInvertedZoom: userCustomSet?.useInvertedZoom ?? false,
      gammaValue: userCustomSet?.userGammaValue ?? 1,
    });
    viewer.refreshUiControls?.();
  }, [
    viewer,
    userCustomSet?.useScaleBar,
    userCustomSet?.useMagnificationBar,
    userCustomSet?.useViewerRotationSlider,
    userCustomSet?.useGammaSlider,
    userCustomSet?.useViewerThumbnail,
    userCustomSet?.useCaptureButton,
    userCustomSet?.useInvertedZoom,
  ]);

  useEffect(() => {
    const labelDiv = document.getElementById('openseadragon-navigator-label');
    if (labelDiv) {
      labelDiv.innerText = description || '';
      labelDiv.style.display = description ? 'block' : 'none';
    }
  }, [description]);

  useEffect(() => {
    if (!viewer || !userCustomSet?.useGammaSlider) return;

    viewer.setGammaValue?.(userCustomSet?.userGammaValue ?? 1);
  }, [viewer, userCustomSet?.userGammaValue, userCustomSet?.useGammaSlider]);

  useEffect(() => {
    if (!viewer) return;

    viewer.onGammaInput = (newValue: number) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        setUserCustomSet((prev: any) => {
          const nextSettings = prev?.userGammaValue === newValue
            ? prev
            : { ...prev, userGammaValue: newValue };

          if (nextSettings !== prev) {
            patchUserCustomSetting(nextSettings).catch((error) => {
              console.error("Failed to update gamma value:", error);
            });
          }

          return nextSettings;
        });
      }, 300);
    };

    return () => {
      viewer.onGammaInput = null;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [viewer, setUserCustomSet]);

  // 줌/팬 핸들러
  useEffect(() => {
    if (viewer) {
      const handleZoomPan = () => {
        if (!annotationInfoRef.current.showEditModal) {
          annotationInfoRef.current.closeAnnotationInfo();
        }
      };

      viewer.addHandler('zoom', handleZoomPan);
      viewer.addHandler('pan', handleZoomPan);

      return () => {
        viewer.removeHandler('zoom', handleZoomPan);
        viewer.removeHandler('pan', handleZoomPan);
      };
    }
  }, [viewer]);

  return (
    <div style={{ position: "relative" }}>
      {loading && (
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(255, 255, 255, 1)",
          zIndex: 1000,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "black",
          fontSize: "1.5rem",
        }}>
          Loading... <LoadingOutlined style={{ marginLeft: "10px" }} />
        </div>
      )}
      <StOpenseadragon
        id="openseadragon"
        style={{ width: "100%", height: "70vh" }}
        $selectedAnnotation={annotationInfo.selectedAnnotation?.annoId}
      />

      {userCustomSet?.useICCButton && (
        <StyledICCButton
          onClick={changeColor}
          $isActive={icc !== "none"}
        >
          ICC
        </StyledICCButton>
      )}

      <div style={{
        position: "absolute",
        bottom: "10px",
        right: "10px",
        display: "flex",
        gap: "4px",
        zIndex: 1000
      }}>
        <ViewerToggleManager mode="slide"/>
      </div>

      <AnnotationInfoPanelComponent {...annotationInfo} />
    </div>
  );
};

export default ConsultationViewer;

interface StOpenseadragonProps {
  $selectedAnnotation?: number;
}

interface StyledICCButtonProps {
  $isActive: boolean;
}

const StyledICCButton = styled(Button)<StyledICCButtonProps>`
  position: absolute;

  background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)'} !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 4px !important;
  top: 5px;
  right: 30.8em;
  font-size: 0.6rem !important;
  padding: 0 0.4rem;
  height: 1.45rem;

  &:hover {
    background-color: ${props => props.$isActive ? 'rgba(24, 144, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'} !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
  }
`;

const StOpenseadragon = styled('div')<StOpenseadragonProps>(({ theme, $selectedAnnotation }) => ({
  width: '100%',
  height: '100%',

  '& .annotation': {
    cursor: 'pointer'
  },

  [`& #annotation${$selectedAnnotation}`]: {
    strokeWidth: '4 !important',
    filter: 'brightness(0.85) drop-shadow(0 0 6px currentColor) drop-shadow(0 0 12px currentColor)',

    '& line': {
      strokeWidth: '4 !important'
    },
    '& path': {
      strokeWidth: '4 !important'
    }
  },

  '& .osd-annotation-label-bg': {
    pointerEvents: 'none',
  },

  '& .osd-annotation-label': {
    fill: 'white !important',
    stroke: 'rgba(0, 0, 0, 0.8) !important',
    pointerEvents: 'none',
    userSelect: 'none',
    paintOrder: 'stroke fill',
    fontWeight: '400 !important',
    fontFamily: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
  }
}));
